import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as signalR from '@microsoft/signalr';
import { API_URL } from '../config/runtimeConfig';
import {
  CUSTOMER_BELL_PAGE_SIZE,
  getCustomerInAppNotificationUnreadCount,
  getCustomerInAppNotifications,
  markAllCustomerInAppNotificationsRead,
  markCustomerChatNotificationsRead,
  markCustomerInAppNotificationRead,
} from '../services/customerInAppNotificationService';
import { syncAppBadge } from '../utils/appBadge';
import { resolveSupportChatHubAccessToken } from '../utils/supportChatThread';

const CustomerNotificationsContext = createContext(null);

const sameId = (left, right) => String(left || '').toLowerCase() === String(right || '').toLowerCase();

const resolveHubUrl = () => {
  const apiBase = (API_URL || '').replace(/\/$/, '');
  const origin = apiBase.replace(/\/api$/i, '');
  return `${origin}/hubs/support-chat`;
};

export const CustomerNotificationsProvider = ({ storeId, children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [badgeCount, setBadgeCount] = useState(null);
  const storeIdRef = useRef(storeId);
  const watchedChatIdRef = useRef(null);
  const acknowledgeSeqRef = useRef(0);
  const acknowledgeChatRef = useRef(() => undefined);
  storeIdRef.current = storeId;

  const refreshNotifications = useCallback(async (nextStoreId) => {
    if (!nextStoreId) {
      setNotifications([]);
      setUnreadCount(0);
      setBadgeCount(0);
      return;
    }

    const [page, unread] = await Promise.all([
      getCustomerInAppNotifications(nextStoreId, { pageIndex: 0, itemsPerPage: CUSTOMER_BELL_PAGE_SIZE }),
      getCustomerInAppNotificationUnreadCount(nextStoreId),
    ]);
    setNotifications(page.data);
    setUnreadCount(unread);
    setBadgeCount(unread);
  }, []);

  useEffect(() => {
    let cancelled = false;
    refreshNotifications(storeId).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [refreshNotifications, storeId]);

  useEffect(() => {
    if (!Number.isInteger(badgeCount)) return undefined;
    void syncAppBadge(badgeCount);
    return undefined;
  }, [badgeCount]);

  useEffect(() => () => {
    void syncAppBadge(0);
  }, []);

  useEffect(() => {
    if (!storeId) return undefined;

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshNotifications(storeIdRef.current).catch(() => undefined);
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshNotifications, storeId]);

  useEffect(() => {
    const token = resolveSupportChatHubAccessToken({ isCustomerSurface: true });
    if (!storeId || !token) return undefined;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(resolveHubUrl(), { accessTokenFactory: () => token })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .build();

    connection.on('ReceiveCustomerNotification', (notification) => {
      if (!notification || !sameId(notification.storeId, storeIdRef.current)) return;
      const chatIsOpen = Boolean(notification.conversationId)
        && sameId(notification.conversationId, watchedChatIdRef.current);
      const incoming = chatIsOpen ? { ...notification, isRead: true } : notification;
      if (!chatIsOpen && Number.isInteger(notification.badgeCount) && notification.badgeCount >= 0) {
        setBadgeCount(notification.badgeCount);
        setUnreadCount(notification.badgeCount);
      }
      setNotifications((current) => {
        if (current.some((item) => sameId(item.id, incoming.id))) return current;
        return [incoming, ...current].slice(0, CUSTOMER_BELL_PAGE_SIZE);
      });
      if (chatIsOpen) {
        acknowledgeChatRef.current(notification.conversationId);
      }
    });

    connection.start().catch(() => undefined);

    return () => {
      connection.stop().catch(() => undefined);
    };
  }, [storeId]);

  const markRead = useCallback(async (notificationId) => {
    const activeStoreId = storeIdRef.current;
    if (!activeStoreId || !notificationId) return;
    const existing = notifications.find((item) => sameId(item.id, notificationId));
    await markCustomerInAppNotificationRead(notificationId, activeStoreId);
    setNotifications((current) => current.map((item) => (
      sameId(item.id, notificationId) ? { ...item, isRead: true } : item
    )));
    if (existing && !existing.isRead) {
      setUnreadCount((count) => Math.max(0, count - 1));
      setBadgeCount((count) => Math.max(0, (Number.isInteger(count) ? count : 0) - 1));
    }
  }, [notifications]);

  const acknowledgeChat = useCallback(async (conversationId) => {
    watchedChatIdRef.current = conversationId || null;
    const activeStoreId = storeIdRef.current;
    if (!activeStoreId || !conversationId) return;
    const seq = acknowledgeSeqRef.current + 1;
    acknowledgeSeqRef.current = seq;
    try {
      const unread = await markCustomerChatNotificationsRead(conversationId, activeStoreId);
      if (seq !== acknowledgeSeqRef.current) return;
      if (!sameId(watchedChatIdRef.current, conversationId)) return;
      setNotifications((current) => current.map((item) => (
        sameId(item.conversationId, conversationId) ? { ...item, isRead: true } : item
      )));
      setUnreadCount(unread);
      setBadgeCount(unread);
    } catch {
      // The next refresh restores the badge if this clear did not land.
    }
  }, []);
  acknowledgeChatRef.current = acknowledgeChat;

  const markAllRead = useCallback(async () => {
    const activeStoreId = storeIdRef.current;
    if (!activeStoreId) return;
    await markAllCustomerInAppNotificationsRead(activeStoreId);
    setNotifications((current) => current.map((item) => ({ ...item, isRead: true })));
    setUnreadCount(0);
    setBadgeCount(0);
  }, []);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    acknowledgeChat,
    refreshNotifications,
  }), [acknowledgeChat, markAllRead, markRead, notifications, refreshNotifications, unreadCount]);

  return (
    <CustomerNotificationsContext.Provider value={value}>
      {children}
    </CustomerNotificationsContext.Provider>
  );
};

export const useCustomerNotifications = () => {
  const context = useContext(CustomerNotificationsContext);
  if (!context) {
    throw new Error('useCustomerNotifications must be used within CustomerNotificationsProvider');
  }
  return context;
};
